import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

// Types
interface Photo {
  id: string;
  src: string;
  width: number;
  height: number;
}

// Auto-import all images from public/photos folder at build time
const imageModules = import.meta.glob<string>(
  '/public/photos/*.{jpg,jpeg,png,webp,avif,JPG,JPEG,PNG,WEBP,AVIF}',
  { eager: true, query: '?url', import: 'default' }
);

// Seeded shuffle for consistent randomization
function seededShuffle<T>(array: T[], seed: number): T[] {
  const shuffled = [...array];
  let currentSeed = seed;

  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor((currentSeed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}


// Get image URLs and shuffle them
const imagePaths: string[] = seededShuffle(
  Object.entries(imageModules)
    .map(([path, url]) => ({
      path,
      url: url as string,
      filename: path.split('/').pop() || path,
    }))
    .sort((a, b) => a.filename.localeCompare(b.filename))
    .map(({ url }) => url),
  42 // seed - change this number to get a different arrangement
);


// Load image and get natural dimensions
function loadImage(src: string): Promise<Photo> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        id: src,
        src,
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };
    img.onerror = reject;
    img.src = src;
  });
}

// Hook to load all photos with dimensions
function usePhotos() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (imagePaths.length === 0) {
      setLoading(false);
      return;
    }

    Promise.all(imagePaths.map(loadImage))
      .then(setPhotos)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return { photos, loading };
}

// ============================================
// LIGHTBOX NAV SETTINGS
// ============================================
const NAV_CONFIG = {
  textOffset: 3.5,        // vw - text distance from edge
  textTriggerZone: 49,    // vw - zone where text becomes visible (2% dead zone in middle)
};

// Lightbox Component
function Lightbox({
  photo,
  onClose,
  onPrev,
  onNext,
  hasPrev,
  hasNext,
  preloadSrcs,
}: {
  photo: Photo;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasPrev: boolean;
  hasNext: boolean;
  preloadSrcs: string[];
}) {
  const [mouseX, setMouseX] = useState(0.5); // 0 = left edge, 1 = right edge
  const [isVisible, setIsVisible] = useState(false);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Delayed UI controls visibility (photo first, then chrome)
  const [uiVisible, setUiVisible] = useState(false);

  // Track which photo has loaded (avoids useEffect race condition)
  const [loadedPhotoId, setLoadedPhotoId] = useState<string | null>(null);
  const imageLoaded = loadedPhotoId === photo.id;

  // Pinch zoom state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const pinchRef = useRef<{ initialDistance: number; initialScale: number } | null>(null);
  const panRef = useRef<{ lastX: number; lastY: number } | null>(null);

  // Track actual viewport height for Chrome iOS tablet fix
  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Underline triggers based on mouse position (within 15vw of edge)
  const underlineThreshold = 0.15;
  const leftUnderline = mouseX < underlineThreshold;
  const rightUnderline = mouseX > (1 - underlineThreshold);

  // Fade in on mount
  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  // Delay UI controls fade-in
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setUiVisible(true), 400);
      return () => clearTimeout(timer);
    } else {
      setUiVisible(false);
    }
  }, [isVisible]);

  // Handle close with fade out (520ms buffer to ensure transition completes)
  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 520);
  };

  // Reset zoom when changing photos
  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [photo.id]);

  // Preload adjacent images so they're cached for quick navigation
  useEffect(() => {
    preloadSrcs.forEach(src => {
      const img = new Image();
      img.src = src;
    });
  }, [preloadSrcs]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
      if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext) onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    // Keep page zoom disabled - we handle image zoom via JS
    document.body.style.touchAction = 'none';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      document.body.style.touchAction = ''; // Reset to CSS default
    };
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  // Mouse tracking
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const x = e.clientX / window.innerWidth;
      setMouseX(x);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Get distance between two touch points
  const getTouchDistance = (touches: React.TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  // Touch handlers for swipe and pinch zoom
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      pinchRef.current = { initialDistance: distance, initialScale: scale };
      panRef.current = null;
    } else if (e.touches.length === 1) {
      if (scale > 1) {
        // Pan when zoomed in
        panRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };
      } else {
        // Swipe navigation when not zoomed
        setTouchStart(e.touches[0].clientX);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && pinchRef.current) {
      // Pinch zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      const newScale = Math.min(4, Math.max(1, pinchRef.current.initialScale * (distance / pinchRef.current.initialDistance)));
      setScale(newScale);

      // Reset translation if zoomed back to 1
      if (newScale <= 1) {
        setTranslate({ x: 0, y: 0 });
      }
    } else if (e.touches.length === 1 && panRef.current && scale > 1) {
      // Pan when zoomed in
      e.preventDefault();
      const dx = e.touches[0].clientX - panRef.current.lastX;
      const dy = e.touches[0].clientY - panRef.current.lastY;
      panRef.current = { lastX: e.touches[0].clientX, lastY: e.touches[0].clientY };

      // Limit pan based on zoom level
      const maxPan = (scale - 1) * 150;
      setTranslate(prev => ({
        x: Math.min(maxPan, Math.max(-maxPan, prev.x + dx)),
        y: Math.min(maxPan, Math.max(-maxPan, prev.y + dy)),
      }));
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    // If was pinching and now less than 2 fingers
    if (pinchRef.current && e.touches.length < 2) {
      pinchRef.current = null;
      // Snap back to 1 if scale is close
      if (scale < 1.1) {
        setScale(1);
        setTranslate({ x: 0, y: 0 });
      }
      return;
    }

    // Single finger swipe for navigation (only when not zoomed)
    if (touchStart !== null && scale <= 1) {
      const touchEnd = e.changedTouches[0].clientX;
      const diff = touchStart - touchEnd;
      const minSwipe = 50;

      if (diff > minSwipe && hasNext) {
        onNext();
      } else if (diff < -minSwipe && hasPrev) {
        onPrev();
      }
    }

    setTouchStart(null);
    panRef.current = null;
  };

  // Calculate opacity: 100% in first 60% from edge, then fade to 0 at trigger boundary
  const textThreshold = NAV_CONFIG.textTriggerZone / 100;
  const fullOpacityZone = 0.6 * textThreshold; // 60% of zone stays at 100%
  const fadeZone = textThreshold - fullOpacityZone; // remaining 40% for fade

  // On first/last photo, show the available nav at full opacity (no mouse-position fading)
  const isFirstPhoto = !hasPrev;
  const isLastPhoto = !hasNext;

  const leftTextOpacity = hasPrev
    ? isLastPhoto
      ? 1
      : mouseX <= fullOpacityZone
        ? 1
        : Math.max(0, 1 - (mouseX - fullOpacityZone) / fadeZone)
    : 0;

  const rightTextOpacity = hasNext
    ? isFirstPhoto
      ? 1
      : (1 - mouseX) <= fullOpacityZone
        ? 1
        : Math.max(0, 1 - ((1 - mouseX) - fullOpacityZone) / fadeZone)
    : 0;

  // Double tap detection for zoom toggle
  const lastTapRef = useRef<number>(0);

  // Click handler based on position - 50% left for prev, 50% right for next
  const handleClick = (e: React.MouseEvent) => {
    // If zoomed in, tap resets zoom
    if (scale > 1) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
      return;
    }

    // Double-tap detection (for touch devices via click)
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap - zoom to 2x at tap location
      setScale(2);
      lastTapRef.current = 0;
      return;
    }
    // Single click - navigate
    const x = e.clientX / window.innerWidth;
    if (x < 0.5 && hasPrev) {
      e.stopPropagation();
      onPrev();
      lastTapRef.current = 0; // Reset so rapid navigation doesn't trigger double-tap zoom
    } else if (x >= 0.5 && hasNext) {
      e.stopPropagation();
      onNext();
      lastTapRef.current = 0; // Reset so rapid navigation doesn't trigger double-tap zoom
    } else {
      // No navigation — record tap for double-tap detection
      lastTapRef.current = now;
    }
  };

  return createPortal(
    <>
    <div
      ref={containerRef}
      className="fixed top-0 left-0 w-full overflow-hidden z-[60] flex items-center justify-center"
      style={{
        height: viewportHeight,
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
        willChange: 'opacity',
        transform: 'translate3d(0, 0, 0)', // Force GPU acceleration for iOS
      }}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Backdrop - COMMENTED OUT FOR GLOBAL OVERLAY TEST */}
      {/* <div className="absolute inset-0 bg-[#1B1B1D]" /> */}

      {/* Close button - desktop only (top right) */}
      <button
        className="absolute z-[70] p-2 text-[#CF6666]/60 hover:text-[#CF6666] hidden lg:block top-6"
        style={{ right: '3.0vw', opacity: uiVisible ? 1 : 0, transition: 'opacity 600ms ease-out, color 150ms' }}
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        aria-label="Close"
      >
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Left text container */}
      <div
        className="absolute z-[67] hidden lg:block cursor-pointer select-none"
        style={{
          left: `${NAV_CONFIG.textOffset}vw`,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: leftTextOpacity * (uiVisible ? 1 : 0),
          transition: 'opacity 1s ease-out',
          WebkitTapHighlightColor: 'transparent',
        }}
        draggable={false}
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <span
          className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px]"
          style={{ color: '#CF6666' }}
        >
          prev
        </span>
        {/* Underline - animates left to right */}
        <div
          style={{
            position: 'absolute',
            bottom: -2,
            left: 0,
            height: 1,
            width: leftUnderline ? '100%' : '0%',
            backgroundColor: '#CF6666',
            opacity: leftTextOpacity,
            transition: 'width 0.4s ease-in-out, opacity 150ms ease-out',
          }}
        />
      </div>

      {/* Right text container */}
      <div
        className="absolute z-[67] hidden lg:block cursor-pointer select-none"
        style={{
          right: `${NAV_CONFIG.textOffset}vw`,
          top: '50%',
          transform: 'translateY(-50%)',
          opacity: rightTextOpacity * (uiVisible ? 1 : 0),
          transition: 'opacity 1s ease-out',
          WebkitTapHighlightColor: 'transparent',
        }}
        draggable={false}
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <span
          className="font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] tracking-[0.2px]"
          style={{ color: '#CF6666' }}
        >
          next
        </span>
        {/* Underline - animates right to left */}
        <div
          style={{
            position: 'absolute',
            bottom: -2,
            right: 0,
            height: 1,
            width: rightUnderline ? '100%' : '0%',
            backgroundColor: '#CF6666',
            opacity: rightTextOpacity,
            transition: 'width 0.4s ease-in-out, opacity 150ms ease-out',
          }}
        />
      </div>

      {/* Image with pinch zoom - key forces fresh element per photo */}
      <img
        key={photo.id}
        ref={imgRef}
        src={photo.src}
        alt=""
        onLoad={() => setLoadedPhotoId(photo.id)}
        className="relative z-[65] max-h-[80vh] md:max-h-[90vh] max-w-[92vw] md:max-w-[82.6vw] object-contain pointer-events-none"
        style={{
          transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
          opacity: imageLoaded ? 1 : 0,
          transition: scale === 1 ? 'transform 200ms ease-out' : 'none',
          touchAction: 'none',
        }}
      />
    </div>

    {/* Mobile/Tablet bottom navigation - fixed to viewport, separate from backdrop */}
    <div
      className="fixed bottom-6 left-0 right-0 z-[100] flex items-center px-6 lg:hidden"
      style={{
        opacity: uiVisible ? 1 : 0,
        transition: 'opacity 600ms ease-out',
      }}
    >
      <div className="flex-1">
        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="flex items-center gap-2 font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] leading-[1] tracking-[0.2px] text-[#CF6666] select-none"
          style={{ opacity: hasPrev ? 1 : 0, transition: 'opacity 400ms ease-out', pointerEvents: hasPrev ? 'auto' : 'none', WebkitTapHighlightColor: 'transparent' }}
        >
          <span style={{ marginBottom: '3px' }}>⟵</span>
          <span>prev</span>
        </button>
      </div>

      {/* Close button - mobile center */}
      <button
        onClick={(e) => { e.stopPropagation(); handleClose(); }}
        className="text-[#CF6666]/60 hover:text-[#CF6666] transition-colors translate-y-0.5"
        aria-label="Close"
      >
        <svg className="w-9 h-9" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex-1 flex justify-end">
        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="flex items-center gap-2 font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[17px] leading-[1] tracking-[0.2px] text-[#CF6666] select-none"
          style={{ opacity: hasNext ? 1 : 0, transition: 'opacity 400ms ease-out', pointerEvents: hasNext ? 'auto' : 'none', WebkitTapHighlightColor: 'transparent' }}
        >
          <span>next</span>
          <span style={{ marginBottom: '3px' }}>⟶</span>
        </button>
      </div>
    </div>
  </>,
    document.body
  );
}

// Image Card Component with staggered image reveal based on Y position
function ImageCard({ photo, onClick, ready, isFirst: _isFirst }: { photo: Photo; onClick: () => void; ready: boolean; isFirst?: boolean }) {
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const [canReveal, setCanReveal] = useState(false);
  // const [showHint, setShowHint] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300%' } // Load 3 viewports ahead
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Stagger based on actual Y position - images higher on page reveal first
  useEffect(() => {
    if (ready && loaded && imgRef.current) {
      const rect = imgRef.current.getBoundingClientRect();
      const scrollTop = window.scrollY;
      const absoluteY = rect.top + scrollTop;
      // Delay based on Y position: ~100ms per 200px down the page
      const delay = Math.floor(absoluteY / 200) * 100;
      const timer = setTimeout(() => setCanReveal(true), delay);
      return () => clearTimeout(timer);
    }
  }, [ready, loaded]);

  // Show "open here to start" hint on first photo after reveal — temporarily disabled
  // useEffect(() => {
  //   if (isFirst && canReveal) {
  //     const timer = setTimeout(() => setShowHint(true), 800);
  //     return () => clearTimeout(timer);
  //   }
  // }, [isFirst, canReveal]);

  return (
    <div
      ref={imgRef}
      className="group relative cursor-pointer overflow-hidden break-inside-avoid mb-4 inline-block w-full align-top"
      onClick={onClick}
    >
      {/* Black placeholder - fades in when ready */}
      <div
        className="bg-[#1B1B1D]"
        style={{
          aspectRatio: `${photo.width}/${photo.height}`,
          opacity: ready ? 1 : 0,
          transition: 'opacity 600ms ease-out',
        }}
      />

      {/* Image - staggered blur fade in (doubled to 1600ms) */}
      {inView && (
        <img
          src={photo.src}
          alt=""
          onLoad={() => setLoaded(true)}
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            opacity: canReveal ? 1 : 0,
            filter: canReveal ? 'blur(0px)' : 'blur(12px)',
            transform: 'scale(1)',
            transition: 'opacity 1600ms ease-out, filter 1600ms ease-out, transform 500ms ease-out',
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        />
      )}

      {/* Hover overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-500 pointer-events-none" />

      {/* "open here to start" hint on first photo — temporarily disabled
      {isFirst && (
        <div
          className="absolute top-3 left-3 font-['arial-narrow',_'Arial_Narrow',_Arial,_sans-serif] text-[15px] tracking-[0.2px] text-[#D88080] pointer-events-none"
          style={{
            opacity: showHint ? 1 : 0,
            transition: 'opacity 800ms ease-out',
          }}
        >
          [open here to start]
        </div>
      )} */}

      {/* Filename on hover - temporarily disabled
      <div
        className="absolute bottom-2 left-2 font-mono text-[12px] text-[#F5F3F1] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
      >
        {decodeURIComponent(photo.src.split('/').pop() || '').replace(/\.[^.]+$/, '')}
      </div>
      */}
    </div>
  );
}

// Main Gallery Component
export function Gallery({ ready = true }: { ready?: boolean }) {
  const { photos, loading } = usePhotos();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handlePrev = useCallback(() => {
    if (selectedIndex !== null && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    }
  }, [selectedIndex]);

  const handleNext = useCallback(() => {
    if (selectedIndex !== null && selectedIndex < photos.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  }, [selectedIndex, photos.length]);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-8 h-8 border-2 border-[#2B2B2B]/20 border-t-[#2B2B2B]/60 rounded-full animate-spin" />
      </div>
    );
  }

  // Empty state
  if (photos.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] text-[#2B2B2B]/50">
        <p className="text-lg">No photos yet</p>
        <p className="text-sm mt-2">Drop images in public/photos/</p>
      </div>
    );
  }

  const isLightboxOpen = selectedIndex !== null;

  return (
    <>
      {/* GLOBAL TEST OVERLAY - always in DOM, visibility controlled by opacity */}
      {/* TEST: 120% size to blast past any viewport gaps */}
      {createPortal(
        <div
          style={{
            position: 'fixed',
            top: '-10vh',
            left: '-10vw',
            width: '120vw',
            height: '120vh',
            backgroundColor: '#1B1B1D', // match artist modal
            zIndex: 59, // just below lightbox content (z-60)
            opacity: isLightboxOpen ? 1 : 0,
            pointerEvents: isLightboxOpen ? 'auto' : 'none',
            transition: 'opacity 500ms ease-in-out',
            transform: 'translate3d(0, 0, 0)', // Force GPU acceleration
          }}
        />,
        document.body
      )}

      {/* Masonry Grid with random spacing */}
      <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 px-4 md:px-8 lg:px-12 py-0">
        {photos.map((photo, index) => (
          <ImageCard
            key={photo.id}
            photo={photo}
            onClick={() => setSelectedIndex(index)}
            ready={ready}
            isFirst={index === 0}
          />
        ))}
      </div>

      {/* Lightbox */}
      {selectedIndex !== null && (
        <Lightbox
          photo={photos[selectedIndex]}
          onClose={() => setSelectedIndex(null)}
          onPrev={handlePrev}
          onNext={handleNext}
          hasPrev={selectedIndex > 0}
          hasNext={selectedIndex < photos.length - 1}
          preloadSrcs={[
            ...(selectedIndex > 0 ? [photos[selectedIndex - 1].src] : []),
            ...(selectedIndex < photos.length - 1 ? [photos[selectedIndex + 1].src] : []),
          ]}
        />
      )}
    </>
  );
}

export default Gallery;
